// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';

type ModalType = 'alert' | 'confirm' | 'prompt' | 'info' | 'choice';

class ModalsComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  visible: boolean = false;
  boxType?: ModalType;
  boxTitle?: string;
  boxMessage?: string;
  boxPlaceholder?: string;
  boxChoices: string[] = [];
  resolveAlertConfirm!: (value: boolean) => void;
  resolvePrompt!: (value: string | false) => void;

  public constructor() {
    super('native-modals');
  }

  private initBox(
    type: ModalType,
    message: string,
    messageHandler: (translatedMessage: string) => string,
    title?: string,
    placeholder?: string,
    choices?: string[]
  ) {
    this.boxType = type;
    this.boxMessage = messageHandler(this.context.i18nManager.getTranslation(message));
    if (title) {
      this.boxTitle = this.context.i18nManager.getTranslation(title);
    }
    if (placeholder) {
      this.boxPlaceholder = this.context.i18nManager.getTranslation(placeholder);
    }
    if (choices) {
      this.boxChoices = choices;
    }

    this.visible = true;
    this.refreshRender();
  }

  private initBoxWithMessageArray(
    type: ModalType,
    message: string | string[],
    messageHandler: (translatedMessage: string | string[]) => string,
    title?: string,
    placeholder?: string
  ) {
    this.boxType = type;
    if (Array.isArray(message)) {
      this.boxMessage = messageHandler(message.map((msg) => this.context.i18nManager.getTranslation(msg)));
    } else {
      this.boxMessage = messageHandler(this.context.i18nManager.getTranslation(message));
    }
    if (title) {
      this.boxTitle = this.context.i18nManager.getTranslation(title);
    }
    if (placeholder) {
      this.boxPlaceholder = this.context.i18nManager.getTranslation(placeholder);
    }

    this.visible = true;
    this.refreshRender();
  }

  private resetBox() {
    this.visible = false;
    this.boxType = undefined;
    this.boxTitle = undefined;
    this.boxMessage = undefined;
    this.boxPlaceholder = undefined;
    this.refreshRender();
  }

  private async promptBox(message: string, title?: string, placeholder?: string): Promise<string | false> {
    this.initBox(
      'prompt',
      message,
      (message: string | string[]) => (Array.isArray(message) ? message[0] : message),
      title,
      placeholder
    );

    return new Promise((resolve) => {
      this.resolvePrompt = resolve;
    });
  }

  private async promptChoiceBox(message: string, title?: string, choices?: string[]): Promise<string | false> {
    this.initBox(
      'choice',
      message,
      (message: string | string[]) => (Array.isArray(message) ? message[0] : message),
      title,
      undefined,
      choices
    );

    return new Promise((resolve) => {
      this.resolvePrompt = resolve;
    });
  }

  private async alertInfoBox(
    message: string | string[],
    title: string,
    messageHandler: (translatedMessage: string | string[]) => string
  ): Promise<boolean> {
    this.initBoxWithMessageArray('info', message, messageHandler, title);

    return new Promise((resolve) => {
      this.resolveAlertConfirm = resolve;
    });
  }

  private async alertConfirmBox(
    type: ModalType,
    message: string,
    title: string,
    messageHandler: (translatedMessage: string) => string
  ): Promise<boolean> {
    this.initBox(type, message, messageHandler, title);

    return new Promise((resolve) => {
      this.resolveAlertConfirm = resolve;
    });
  }

  public ok() {
    this.resetBox();
    this.resolveAlertConfirm(true);
  }

  public nok() {
    this.resetBox();
    this.resolveAlertConfirm(false);
  }

  public promptOk() {
    const input = this.shadow.getElementById('input-text') as HTMLInputElement;
    const text = input.value;
    input.value = '';
    this.resetBox();
    this.resolvePrompt(text);
  }

  public choiceOk(choice: string) {
    this.resetBox();
    this.resolvePrompt(choice);
  }

  public promptNok() {
    this.resetBox();
    this.resolvePrompt(false);
  }

  override render() {
    super.render();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    const defaultMessageHandler = (translatedMessage: string) => translatedMessage;
    const defaultMessageArrayHandler = (translatedMessage: string | string[]) =>
      Array.isArray(translatedMessage) ? translatedMessage[0] : translatedMessage;

    window.gConfirm = (
      message: string,
      title: string,
      messageHandler?: (translatedMessage: string) => string
    ): Promise<boolean> => this.alertConfirmBox('confirm', message, title, messageHandler ?? defaultMessageHandler);

    window.gAlert = (message: string, title?: string): Promise<boolean> =>
      this.alertConfirmBox('alert', message, title ?? '', defaultMessageHandler);

    window.gPrompt = (message: string, title?: string, placeholder?: string): Promise<string | false> =>
      this.promptBox(message, title, placeholder);

    window.gPromptChoice = (message: string, title?: string, choices?: string[]): Promise<string | false> =>
      this.promptChoiceBox(message, title, choices);

    window.gInfo = (
      message: string | string[],
      title: string,
      messageHandler?: (translatedMessage: string | string[]) => string
    ): Promise<boolean> => this.alertInfoBox(message, title, messageHandler ?? defaultMessageArrayHandler);

    window.gCloseModal = () => {
      this.ok();
    };

    this.render();
    super.girafeTranslate();
  }
}

export default ModalsComponent;
